import config from './config.js';

/**
 * Returns the breakpoint specific sub object for the current breakpoint size.
 * Example: colObject={xs:'...',lg:'..'}, vpSize=sm ==> returns the value defined for xs, since lower breakpoints are valid for larger sizes but get overwritten by larger size settings.
 * @param {*} colObject
 * @param {*} vpSize
 */
export function rCols(colObject, breakpointName) {
  //console.log('rCols', colObject, breakpointName);
  switch (breakpointName) {
    case 'xs':
      return colObject.xs ? colObject.xs : 1;
    case 'sm':
      return colObject.sm ? colObject.sm : rCols(colObject, 'xs');
    case 'md':
      return colObject.md ? colObject.md : rCols(colObject, 'sm');
    case 'lg':
      return colObject.lg ? colObject.lg : rCols(colObject, 'md');
    case 'xl':
      return colObject.xl ? colObject.xl : rCols(colObject, 'lg');
  }
}

/**
 * Pushes the item to the array named [attributeName]. If the array is undefined, it'll be created.
 * @param {Object} object The object to push the item
 * @param {String} attributeName the name of the array attribute
 * @param {Object} item the item to push
 */
export function push(object, attributeName, item) {
  if (object) {
    if (!object[attributeName]) {
      object[attributeName] = [];
    }
    object[attributeName].push(item);
  }
}

/**
 * Pushes the item to the array stored in object[attributeName]. Creates a new array if there is none.
 * If there is an object listed identified by the same [identityAttribute] it is replaced
 * @param {*} object
 * @param {*} attributeName
 * @param {*} identityAttribute
 * @param {*} item
 * @returns
 */
export function pushOrReplace(object, attributeName, identityAttribute, item) {
  if (object) {
    if (!object[attributeName]) {
      object[attributeName] = [];
    }
    const targetArray = object[attributeName];
    // find out if the target array contains an item with the same identityAttribute value as the item
    let i = 0;
    while (i < targetArray.length) {
      if (targetArray[i][identityAttribute] === item[identityAttribute]) {
        // replace
        targetArray[i] = item;
        //console.debug('Replace item with index i at', i);
        return;
      }
      i++;
    }
    targetArray.push(item);
  }
}

export async function delay(millis) {
  return new Promise((resolve) => {
    setTimeout(() => resolve(), millis);
  });
}

export function orDefault(value, defaultValue) {
  return value === undefined ? defaultValue : value;
}

/**
 *
 * @param { string } str the possible UUID
 * @returns
 */
export function isValidUUID(str) {
  const regexExp = /^[0-9a-fA-F]{8}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{12}$/gi;

  return regexExp.test(str);
}

/**
 * Returns a ArrayBuffer containing an ECDSA P-256 public key that the push server will use to
 * authenticate your application server. If specified, all messages from the application server must use the
 * VAPID authentication scheme, and include a JWT signed with the corresponding private key.
 *
 * @param base64String
 * @returns {Uint8Array}
 */
export function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/\-/g, '+').replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

// Returns a function, that, as long as it continues to be invoked, will not
// be triggered. The function will be called after it stops being called for
// time milliseconds.
export const debounce = (fn, time) => {
  let timeout;

  return (...args) => {
    const functionCall = () => fn.apply(this, args);

    clearTimeout(timeout);
    timeout = setTimeout(functionCall, time);
  };
};

// Returns a function, that, as long as it continues to be invoked, will not
// be triggered. The function will be called after it stops being called for
// time milliseconds.
export const debounceFirst = (fn, time) => {
  let timeout;
  let firstCalled = false;

  return (...args) => {
    const functionCall = () => fn.apply(this, args);

    clearTimeout(timeout);
    timeout = setTimeout(functionCall, time);
    if (!firstCalled) {
      functionCall();
      firstCalled = true;
    }
  };
};
const getRandomString = () => Math.random().toString(36).substring(2, 15);

export default {
  /**
   * Rounds a number to {digits}
   * @param value - the value to round
   * @param digits - the number of digits after the fraction
   * @returns {number} - rounded number
   */
  round(value, digits) {
    const f = Math.pow(10, digits);
    return Math.round(value * f) / f;
  },
  getRandomString,
  logDataOutline(scope, data) {
    Object.keys(data).forEach((k) => {
      let obj = data[k];
      if (Array.isArray(obj)) {
        console.debug(`${scope}.${k}:${obj.length} items`);
      } else {
        console.debug(`${scope}.${k}:${Object.keys(obj).length} properties`);
      }
    });
  },
  /**
   * Constructs the backend url used to download a file (document, video, etc.). No caching parameters set.
   * @param {*} moduleBackendId - the rest api path segment to specify the module, e.g. /medias/${moduleBackendId}/... will be /medias/documents/... for document module
   * @param {*} file - the file object
   * @param {*} filenameAttribute - the attribute in file object containing the filename
   * @param {*} requestSuffix - there might be a suffix for special cases (see in forms module store)
   * @returns the fully qualified url
   */
  constructFileUrlNoCaching(moduleBackendId, file, filenameAttribute = 'filename', requestSuffix = '') {
    return `${config.apiUrl}/medias/${moduleBackendId}${requestSuffix}/${file[filenameAttribute]}/${file.checksum}`;
  },
  /**
   * Constructs the backend url used to download a file (document, video, etc.). Caching hints are set. The service-worker is instructed to cache files when these are set.
   * @param {*} moduleBackendId - the rest api path segment to specify the module, e.g. /medias/${moduleBackendId}/... will be /medias/documents/... for document module
   * @param {*} file - the file object
   * @param {*} filenameAttribute - the attribute in file object containing the filename
   * @param {*} requestSuffix - there might be a suffix for special cases (see in forms module store)
   * @returns the fully qualified url
   */
  constructFileUrl(moduleBackendId, file, filenameAttribute = 'filename', requestSuffix = '') {
    // return `${config.apiUrl}/medias/${moduleBackendId}${requestSuffix}/${file[filenameAttribute]}/${file.checksum}?caching=true&id=${file.id}`;
    return `${config.apiUrl}/medias/${moduleBackendId}${requestSuffix}/${file[filenameAttribute]}/${file.checksum}`;
  },
  debounce,
  debounceFirst,

  /**
   * Checks if the restrictedToMembersOfGroup parameter contains one of the provided groups.
   * If restrictedToMembersOfGroup is empty, the access is not restricted
   * @param groups
   * @param restrictedToMembersOfGroup - comma separated list of permitted groups
   * @returns {*|boolean} - true to indicate the access is given
   */
  isAccessibleByRestrictedToMembersOfGroup(groups, restrictedToMembersOfGroup) {
    if (!restrictedToMembersOfGroup) return true; // no restrictions
    const found = restrictedToMembersOfGroup.split(',').find((g) => {
      return groups.includes(g);
    });
    console.debug('found restrictedToMembersOfGroup', found);
    return !!found;
  },
};
